import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  getStoredToken,
  getStoredRefreshToken,
  setStoredTokens,
  getStoredUser,
  setStoredUser as saveStoredUser,
} from '../services/apiClient';
import {
  clearCsrfToken,
  logout as authLogout,
  fetchUserProfile,
  bootstrapAuthSession,
} from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function readInitialAuthFromStorage() {
  if (typeof window === 'undefined') {
    return { token: '', storedUser: null };
  }
  const token = (getStoredToken() || '').trim();
  const storedUser = getStoredUser();
  return { token, storedUser };
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!readInitialAuthFromStorage().token);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionChecking, setIsSessionChecking] = useState(false);
  const [user, setUser] = useState(() => readInitialAuthFromStorage().storedUser);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const refreshTokenValue = getStoredRefreshToken();
        const accessToken = getStoredToken();

        if (!refreshTokenValue && (!accessToken || accessToken.trim() === '')) {
          setIsAuthenticated(false);
          setUser(null);
          clearCsrfToken();
          setStoredTokens({ token: null, refreshToken: null, sessionId: null });
          setIsLoading(false);
          return;
        }

        const storedUser = getStoredUser();

        try {
          setIsSessionChecking(true);
          let userData;
          if (refreshTokenValue) {
            userData = await bootstrapAuthSession();
          } else {
            userData = await fetchUserProfile();
          }

          const basarili = userData?.basarili !== false;

          if (basarili && userData) {
            const veri = userData?.veri;
            const k = veri?.kullanici || userData?.veri || userData?.kullanici || userData || null;
            let merged = null;
            if (k && typeof k === 'object' && !Array.isArray(k)) {
              merged = { ...(storedUser || {}), ...k };
            } else {
              merged = k;
            }
            if (merged && typeof merged === 'object' && !Array.isArray(merged)) {
              merged = { ...(storedUser || {}), ...merged };
              if (!merged.rol && storedUser?.rol) merged.rol = storedUser.rol;
            }
            setUser(merged);
            if (merged) saveStoredUser(merged);
            setIsAuthenticated(true);
          } else {
            const fallbackUser = storedUser || { kullaniciAdi: 'Kullanıcı' };
            setUser(fallbackUser);
            setIsAuthenticated(true);
          }
        } catch (error) {
          if (error?.status === 429) {
            const fallbackUser = storedUser || { kullaniciAdi: 'Kullanıcı' };
            setUser(fallbackUser);
            setIsAuthenticated(true);
          } else if (error?.status === 401 || error?.status === 403) {
            setStoredTokens({ token: null, refreshToken: null, sessionId: null });
            saveStoredUser(null);
            clearCsrfToken();
            setIsAuthenticated(false);
            setUser(null);
          } else if (accessToken || refreshTokenValue) {
            const fallbackUser = storedUser || { kullaniciAdi: 'Kullanıcı' };
            setUser(fallbackUser);
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            setUser(null);
          }
        } finally {
          setIsSessionChecking(false);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
        clearCsrfToken();
        setStoredTokens({ token: null, refreshToken: null, sessionId: null });
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setIsLoading(false);
    if (userData && typeof userData === 'object') {
      saveStoredUser(userData);
    }
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch {
      /* ignore */
    } finally {
      saveStoredUser(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshProfile = async () => {
    const userData = await fetchUserProfile();
    const basarili = userData?.basarili !== false;
    if (!basarili || !userData) return null;
    const veri = userData?.veri;
    const k = veri?.kullanici;
    if (!k || typeof k !== 'object' || Array.isArray(k)) return null;
    const stored = getStoredUser();
    let merged = { ...(stored || {}), ...k };
    if (!merged.rol && stored?.rol) merged.rol = stored.rol;
    setUser(merged);
    saveStoredUser(merged);
    return merged;
  };

  const value = {
    isAuthenticated,
    isLoading,
    isSessionChecking,
    user,
    login,
    logout,
    setUser,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
